import type { Map as MapboxMap, PaddingOptions } from 'mapbox-gl'

import { onlyDefinedProperties } from 'lib/object/objectUtils'
import { mapBoundsToMapboxBounds, mapboxBoundsToMapBounds } from './bounds'
import type {
  FitMapBoundsOptions,
  LngLat,
  MapHandle,
  MapPadding,
  MapViewport,
  PanMapByOptions,
  ScreenPoint,
  SetMapViewportOptions,
} from './types'

function toMapboxPadding(padding?: number | MapPadding): number | PaddingOptions | undefined {
  if (typeof padding === 'number' || padding === undefined) {
    return padding
  }

  return {
    top: padding.top,
    bottom: padding.bottom,
    left: padding.left,
    right: padding.right,
  }
}

function getViewportCameraOptions(viewport: MapViewport) {
  return onlyDefinedProperties({
    center: [viewport.longitude, viewport.latitude] as [number, number],
    zoom: viewport.zoom,
    bearing: viewport.bearing,
    pitch: viewport.pitch,
    padding: toMapboxPadding(viewport.padding),
  })
}

function panOptions(options?: PanMapByOptions) {
  return {
    duration: options?.durationMs ?? 0,
    essential: options?.essential,
  }
}

export function createMapboxMapHandle(map: MapboxMap): MapHandle {
  return {
    getBounds() {
      const bounds = map.getBounds()
      return bounds ? mapboxBoundsToMapBounds(bounds) : undefined
    },

    getZoom() {
      return map.getZoom()
    },

    fitBounds(bounds, options?: FitMapBoundsOptions) {
      map.fitBounds(mapBoundsToMapboxBounds(bounds), {
        padding: toMapboxPadding(options?.padding),
        maxZoom: options?.maxZoom,
        duration: options?.durationMs ?? 0,
        essential: options?.essential,
      })
    },

    setViewport(viewport: MapViewport, options?: SetMapViewportOptions) {
      const cameraOptions = getViewportCameraOptions(viewport)

      if (options?.animate) {
        map.easeTo({
          ...cameraOptions,
          duration: options.durationMs ?? 0,
          essential: options.essential,
        })
        return
      }

      map.jumpTo(cameraOptions)
    },

    panBy(offset: ScreenPoint, options?: PanMapByOptions) {
      map.panBy([offset.x, offset.y], panOptions(options))
    },

    project(coordinate: LngLat) {
      const projected = map.project([coordinate.lng, coordinate.lat])

      return {
        x: projected.x,
        y: projected.y,
      }
    },

    unproject(point: ScreenPoint) {
      const coordinate = map.unproject([point.x, point.y])

      return {
        lat: coordinate.lat,
        lng: coordinate.lng,
      }
    },

    getContainer() {
      return map.getContainer()
    },
  }
}
